import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Info } from 'lucide-react';
import { cn } from './lib/utils';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import AnnouncementPopup from './components/home/AnnouncementPopup';
import Home from './pages/Home';
import Team from './pages/Team';
import FindVej from './pages/FindVej';
import Services from './pages/Services';
import PraktiskInfo from './pages/PraktiskInfo';
import Contact from './pages/Contact';
import InfoPage from './pages/InfoPage';
import NyPatient from './pages/NyPatient';
import Konsultationer from './pages/Konsultationer';
import Graviditet from './pages/Graviditet';
import Boerneundersoegelser from './pages/Boerneundersoegelser';
import Referrals from './pages/Referrals';
import Screening from './pages/Screening';
import RejseVaccinationer from './pages/RejseVaccinationer';
import RisOgRos from './pages/RisOgRos';
import ChronicConditions from './pages/ChronicConditions';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    // Check if the user has already dismissed this specific announcement in the current session
    const isDismissed = sessionStorage.getItem('announcement-falsk-sms-minlaege-2026-dismissed');
    if (!isDismissed) {
      // Show popup with a slight delay for a better user experience
      const timer = setTimeout(() => {
        setIsPopupOpen(true);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    sessionStorage.setItem('announcement-falsk-sms-minlaege-2026-dismissed', 'true');
  };

  const handleOpenPopup = () => {
    setIsPopupOpen(true);
  };

  return (
    <HelmetProvider>
      <div className="min-h-screen bg-white flex flex-col font-sans text-slate-900 selection:bg-teal-100 selection:text-teal-900">
        <ScrollToTop />
        <Header />
        <div className="flex-grow">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/hvem-er-vi" element={<Team />} />
            <Route path="/find-vej" element={<FindVej />} />
            <Route path="/screening" element={<Screening />} />
            <Route path="/rejse-vaccinationer" element={<RejseVaccinationer />} />
            <Route path="/ris-og-ros" element={<RisOgRos />} />
            <Route path="/kroniske-lidelser" element={<ChronicConditions />} />
            <Route path="/ydelser-og-priser" element={<Services />} />
            <Route path="/praktisk-info" element={<PraktiskInfo />} />
            <Route path="/patient-info" element={<NyPatient />} />
            <Route path="/konsultationer" element={<Konsultationer />} />
            <Route path="/gravide" element={<Graviditet />} />
            <Route path="/boerneundersoegelser" element={<Boerneundersoegelser />} />
            <Route path="/henvisninger" element={<Referrals />} />
            <Route path="/kontakt" element={<Contact />} />
            <Route path="/nyhed/:slug" element={<InfoPage />} />
            <Route path="/:slug" element={<InfoPage />} />
          </Routes>
        </div>
        <Footer />

        {/* Global Announcement Popup */}
        <AnnouncementPopup isOpen={isPopupOpen} onClose={handleClosePopup} />

        {/* Floating button to reopen popup - visible on all pages */}
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.2 }}
          onClick={handleOpenPopup}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2.5 bg-[#4b717a] hover:bg-[#3d5c64] text-white px-5 py-3.5 rounded-full shadow-xl hover:shadow-2xl transition-all hover:scale-105 active:scale-95 cursor-pointer border border-white/20"
          title="Vigtig information fra klinikken"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-400"></span>
          </span>
          <span className="text-sm font-semibold tracking-wide pr-1">Vigtig meddelelse</span>
        </motion.button>
      </div>
    </HelmetProvider>
  );
}

export default App;
